import { useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { useStudio } from '@/components/studio/StudioContext'
import { CollapsibleSection, SelectParam } from './settings-sections'

export interface FrameOutputNodeSettingsProps {
  nodeId: string
  state: Record<string, unknown>
  onStateChange: (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => void
}

/**
 * Configuration for a Frame Output node.
 *
 * Which result to save stays on the node face, because it depends on the live
 * incoming connection. The destination frame and the parent version it appends
 * to are configuration, so they live here.
 */
export function FrameOutputNodeSettings({ state, onStateChange }: FrameOutputNodeSettingsProps) {
  const studio = useStudio()

  const frames = studio.project?.artboards ?? []
  const frame = frames.find(f => f.id === state.frameId)
  const activeVersionId = frame?.versions[frame.activeVersion]?.id ?? null
  // The node appends onto the version that was current when the frame was picked.
  // If Artboard has since moved on, appending would branch off an older parent.
  const parentDrifted = Boolean(frame) && state.expectedVersionId !== activeVersionId && !state.appliedVersionId

  const handleFrameChange = useCallback((frameId: string) => {
    const next = frames.find(f => f.id === frameId)
    onStateChange(prev => ({
      ...prev,
      frameId: next?.id ?? '',
      expectedVersionId: next?.versions[next.activeVersion]?.id ?? null,
      resultVersionId: crypto.randomUUID(),
      appliedVersionId: null,
    }))
  }, [frames, onStateChange])

  const useCurrentAsParent = useCallback(() => {
    onStateChange(prev => ({ ...prev, expectedVersionId: activeVersionId }))
  }, [activeVersionId, onStateChange])

  return (
    <CollapsibleSection title="Destination frame">
      <div className="flex flex-col gap-3 py-2">
        <SelectParam
          label="Append to frame"
          value={frame?.id ?? ''}
          onChange={handleFrameChange}
          placeholder={frames.length ? 'Choose a frame' : 'No frames yet'}
          options={frames.map(f => ({ value: f.id, label: f.title }))}
        />

        {frame && (
          <p className="px-2 text-xs text-muted-foreground">
            Saving creates a new version. Earlier versions remain available.
          </p>
        )}

        {parentDrifted && (
          <div role="status" className="mx-2 flex gap-2 rounded-md bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
            <Icon name="info" className="mt-px h-3.5 w-3.5 shrink-0" />
            <div className="flex flex-col items-start gap-1.5">
              <span>Artboard has a newer version than the one this output branches from.</span>
              <button type="button" onClick={useCurrentAsParent} className="font-medium underline underline-offset-2">
                Use the current frame version as the parent
              </button>
            </div>
          </div>
        )}

        <div className="px-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            disabled={!frame}
            onClick={() => frame && studio.showFrame(frame.id)}
          >
            <Icon name="frame" className="mr-2 h-4 w-4" />
            Show in Artboard
          </Button>
        </div>
      </div>
    </CollapsibleSection>
  )
}
